import './styles/main.css';
import { effect, signal } from '@preact/signals';
import { render, h } from 'preact';
import { AudioEngine } from './audio/AudioEngine';
import { EventEffects } from './core/EventEffects';
import { GameController } from './core/GameController';
import { PlayController } from './core/PlayController';
import { loadSettings, sanitizePlayerName, saveSettings, type Settings } from './core/settings';
import { MAX_OFFLINE_SECONDS } from './game/constants';
import { browserStorage, deleteSave, loadGame, saveGame } from './game/save';
import { advance, isNight } from './game/simulation';
import { createInitialState, type GameState } from './game/state';
import { InputManager } from './input/InputManager';
import { NetManager } from './net/NetManager';
import { PeerNetwork } from './net/peerTransport';
import { isValidRoomCode, normalizeRoomCode } from './net/transport';
import { WorldRenderer } from './render/WorldRenderer';
import { App } from './ui/App';
import type { Services } from './ui/context';
import { openPanel, toast, ui } from './ui/store';
import { selectTool } from './ui/components/Hotbar';

const AUTOSAVE_MS = 10_000;
/** Frames longer than this are treated as a pause and fast-forwarded. */
const CATCH_UP_THRESHOLD_S = 0.5;

function showFatal(message: string): void {
  const root = document.getElementById('ui');
  if (!root) return;
  root.innerHTML = '';
  const box = document.createElement('div');
  box.className = 'fatal';
  box.textContent = message;
  root.appendChild(box);
}

/** Fast-forwards a farm and returns how many flowers bloomed meanwhile. */
function catchUp(state: GameState, seconds: number): number {
  const events = advance(state, Math.min(seconds, MAX_OFFLINE_SECONDS), { authoritative: true });
  return events.filter((e) => e.type === 'bloomed').length;
}

function readJoinParam(): void {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('join');
  if (!raw) return;
  const code = normalizeRoomCode(raw);
  if (isValidRoomCode(code)) ui.pendingJoin.value = code;
  params.delete('join');
  const query = params.toString();
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
  );
}

function bootstrap(): void {
  const storage = browserStorage();
  const settings = signal<Settings>(loadSettings(storage));
  const loaded = loadGame(storage);
  const game = new GameController(loaded?.state ?? createInitialState(), 'local');
  const offlineBlooms =
    loaded && loaded.offlineSeconds > 30 ? catchUp(game.state, loaded.offlineSeconds) : 0;
  readJoinParam();

  const container = document.getElementById('world');
  if (!container) throw new Error('#world container missing');
  let world: WorldRenderer;
  try {
    world = new WorldRenderer(container, settings.value.quality);
  } catch (error) {
    console.error(error);
    showFatal(
      'Petal Plains needs WebGL to grow its flowers 🌱 — please try a different browser or device.',
    );
    return;
  }
  world.rig.attract = true;
  world.rig.distance = 30;

  const input = new InputManager(world.canvas);
  const audio = new AudioEngine();
  const play = new PlayController(game, world, input, audio, settings.value.playerColor);
  new EventEffects(game, world, play, audio);

  // The farm is paused on the title screen; that time is caught up on start.
  // Until then we don't autosave, so a later reload still counts it as offline.
  const bootedAt = Date.now();
  const saveNow = () => {
    if (ui.started.value && game.mode !== 'client') saveGame(storage, game.state);
  };

  // While visiting a friend we keep our own farm safe in memory (and on disk).
  let home: { state: GameState; leftAt: number } | null = null;
  const net = new NetManager(new PeerNetwork(), game, play, {
    identity: () => ({ name: settings.value.playerName, color: settings.value.playerColor }),
    beforeVisit: () => {
      saveNow();
      home = { state: game.state, leftAt: Date.now() };
    },
    restoreOwnFarm: () => {
      const own = home?.state ?? loadGame(storage)?.state ?? createInitialState();
      // If the visit never started (join failed) our farm kept ticking all along.
      if (home && game.state !== home.state) catchUp(own, (Date.now() - home.leftAt) / 1000);
      home = null;
      game.replaceState(own);
      saveNow();
    },
  });
  play.setIdentity(settings.value.playerName, settings.value.playerColor, false);

  const updateSettings = (patch: Partial<Settings>) => {
    const next = { ...settings.value, ...patch };
    if (patch.playerName !== undefined)
      next.playerName = sanitizePlayerName(patch.playerName) || settings.value.playerName;
    settings.value = next;
    saveSettings(storage, next);
  };

  effect(() => {
    const s = settings.value;
    audio.setVolumes(s.musicVolume, s.sfxVolume, s.muted);
  });
  effect(() => {
    input.setEnabled(
      ui.started.value &&
        ui.panel.value === null &&
        !ui.chatOpen.value &&
        ui.levelUp.value === null,
    );
  });

  const startGame = () => {
    if (ui.started.value) return;
    ui.started.value = true;
    const titleBlooms = catchUp(game.state, (Date.now() - bootedAt) / 1000);
    game.changed.emit();
    world.rig.attract = false;
    world.rig.distance = 21;
    audio.unlock();
    audio.startMusic();
    const blooms = offlineBlooms + titleBlooms;
    if (blooms > 0)
      toast(
        `🌙 While you were away, ${blooms} flower${blooms === 1 ? '' : 's'} bloomed!`,
        'good',
        5,
      );
    else if (!loaded) toast('🌼 Welcome! Click the soil to plant your first seeds.', 'good', 6);
  };

  const services: Services = {
    game,
    play,
    net,
    audio,
    settings,
    updateSettings,
    startGame,
    resetFarm: () => {
      deleteSave(storage);
      game.replaceState(createInitialState());
      openPanel(null);
      toast('🌱 A fresh start on a brand new farm!', 'good');
    },
    hasSave: loaded !== null,
  };

  const uiRoot = document.getElementById('ui');
  if (!uiRoot) throw new Error('#ui container missing');
  render(h(App, { services }), uiRoot);
  document.getElementById('loading')?.remove();

  input.commands.on((command) => {
    switch (command) {
      case 'act':
        play.act();
        break;
      case 'tool1':
        selectTool('hand');
        break;
      case 'tool2':
        selectTool('water');
        break;
      case 'tool3':
        selectTool('seeds');
        break;
      case 'tool4':
        selectTool('shovel');
        break;
      case 'escape':
        if (ui.levelUp.value !== null) ui.levelUp.value = null;
        else openPanel(null);
        break;
      case 'chat':
        if (ui.started.value && net.active && ui.panel.value === null) ui.chatOpen.value = true;
        break;
    }
  });

  // Browsers only allow audio after a user gesture.
  const unlockAudio = () => {
    audio.unlock();
    if (ui.started.value) audio.startMusic();
  };
  window.addEventListener('pointerdown', unlockAudio, { once: true });
  window.addEventListener('keydown', unlockAudio, { once: true });

  // Re-render the HUD when state changes, and at least a few times a second
  // for clocks and growth bars.
  let uiDirty = true;
  let lastUiBump = 0;
  game.changed.on(() => (uiDirty = true));

  let last = performance.now();
  const step = (now: number) => {
    const elapsed = (now - last) / 1000;
    last = now;
    let dt = elapsed;
    if (elapsed > CATCH_UP_THRESHOLD_S) {
      // The tab was hidden or throttled: fast-forward instead of one giant tick.
      if (ui.started.value && game.isAuthority) {
        const blooms = catchUp(game.state, elapsed);
        if (elapsed > 60 && blooms > 0)
          toast(
            `🌙 While you were away, ${blooms} flower${blooms === 1 ? '' : 's'} bloomed!`,
            'good',
          );
        game.changed.emit();
      }
      dt = 1 / 60;
    }
    if (ui.started.value) game.update(dt);
    net.update(Date.now());
    if (uiDirty || now - lastUiBump > 250) {
      uiDirty = false;
      lastUiBump = now;
      ui.version.value++;
    }
    return dt;
  };

  const frame = (now: number) => {
    const dt = step(now);
    play.update(dt);
    world.update(dt, game.state, play.position);
    audio.setNight(isNight(game.state.dayTime));
    world.render();
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);

  // rAF stops in background tabs; keep hosts and solo farms ticking slowly.
  setInterval(() => {
    if (document.hidden) step(performance.now());
  }, 1000);

  setInterval(saveNow, AUTOSAVE_MS);
  document.addEventListener('visibilitychange', () => document.hidden && saveNow());
  window.addEventListener('pagehide', saveNow);

  if (import.meta.env.DEV) {
    (window as unknown as Record<string, unknown>).__petal = { game, world, play, net, ui };
  }
}

bootstrap();
